interface HeaderSearch {
  name: LangString
  value: string
  placeholder: LangString
  path?: string
  url: string
}

interface Service {
  name: LangString
  shortName: LangString
  hashTag: LangString
  searches?: HeaderSearch[]
}

const brandName: LangString = {
  ja: '国立国会図書館',
  en: 'National Diet Library',
}

export const TITLE_SPLITTER = ' - '

export const useSite = () => {
  const config = useRuntimeConfig()
  const { ls, isTopFunc } = useI18n()
  const sites: { [key in ServiceType]: Service } = {
    jps: {
      name: { ja: 'ジャパンサーチ', en: 'Japan Search' },
      shortName: { ja: 'ジャパンサーチ', en: 'Japan Search' },
      hashTag: { ja: 'ジャパンサーチ', en: 'JapanSearch' },
      searches: [
        {
          value: 'book',
          name: { ja: '資料を検索', en: 'Search materials' },
          placeholder: {},
          url: '/search?cs=bib&keyword=',
        },
        {
          value: 'site',
          name: { ja: 'サイト内を検索', en: 'Search website' },
          placeholder: {},
          url: '/site/search?cs=page&keyword=',
        },
      ],
    },
    hina: {
      name: { ja: '国立国会図書館東日本大震災アーカイブ', en: 'National Diet Library Great East Japan Earthquake Archive' },
      shortName: { ja: '国立国会図書館東日本大震災アーカイブ', en: 'National Diet Library Great East Japan Earthquake Archive' },
      hashTag: { ja: '国立国会図書館東日本大震災アーカイブ', en: 'NDL_HINAGIKU' },
      searches: [
        {
          value: 'site',
          name: { ja: 'サイト内', en: 'Website' },
          placeholder: { ja: 'サイト全体を検索', en: 'Search website' },
          url: '/site/search?cs=web&keyword=',
        },
        {
          value: 'book',
          name: { ja: '資料', en: 'Materials' },
          placeholder: { ja: '本など資料を検索', en: 'Search materials' },
          url: config.public.iosBaseUrl + '/search?cs=bib&keyword=',
        },
      ],
    },
  }

  const site = sites[config.public.serviceType]
  const route = useRoute()

  const siteName = (): string => {
    return ls(site.name)
  }

  const siteNameShort = (): string => {
    return ls(site.shortName)
  }

  const getHashTag = (): string => {
    return ls(site.hashTag)
  }

  const matchSubPath = (path: string) => {
    const nPath = getNonLangPath(route.path)
    return nPath === path || nPath.startsWith(path + '/')
  }

  const getSearchOptions = (): HeaderSearch[] => {
    const searchOpts = []
    if (site.searches) {
      searchOpts.push(
        ...site.searches.filter(s => !s.path || matchSubPath(s.path)),
      )
    }
    return searchOpts
  }

  const getTitle = (title: LangString): string => {
    //  NOTE: $isTopのpluginで判定しようとすると、タイミング的に遅いため、
    //  ここでは明示的に、同じロジックを呼び出す
    if (isTopFunc(route.path)) {
      return siteName()
    }
    const nPath = getNonLangPath(route.path)
    const titles = []
    if (title) {
      titles.push(ls(title))
    }
    titles.push(ls(site.shortName))
    return titles.join(TITLE_SPLITTER)
  }

  const hostUrl = (): string => {
    return config.public.serviceBaseUrl
  }

  const fullSiteLink = (serviceType: ServiceType, path: string) => {
    let prefix = ''
    switch (serviceType) {
      case 'jps':
        prefix = config.public.jpsBaseUrl
        break
      case 'hina':
        prefix = config.public.hinaBaseUrl
        break
    }
    return prefix + path
  }

  const fullPageLink = (encode = true): string => {
    if (encode) {
      if (route.fullPath === route.path) {
        //  URL上にパラメータやハッシュが存在しない場合、全てをEncodeして返す。
        return encodeURIComponent(hostUrl() + route.fullPath)
      } else {
        //  URL上にパラメータやハッシュが存在する場合、
        //  Vue RouterのfullPathは、エンコードされた値が格納されるため、
        //  パス部分を明示的にEncodeする。
        const base = encodeURIComponent(hostUrl() + route.path)
        return route.fullPath.replace(route.path, base)
      }
    } else if (route.fullPath === route.path) {
      //  URL上にパラメータやハッシュが存在しない場合、全てをそのまま返す。
      return hostUrl() + route.path
    } else {
      //  URL上にパラメータやハッシュが存在する場合、
      //  Vue RouterのfullPathは、エンコードされた値が格納されるため、
      //  パラメータを明示的にdecodeする。
      const param = route.fullPath.replace(route.path, '')
      return hostUrl() + route.path + encodeURI(decodeURIComponent(param))
    }
  }

  return {
    siteName,
    getTitle,
    getHashTag,
    siteNameShort,
    getSearchOptions,
    hostUrl,
    fullSiteLink,
    fullPageLink,
  }
}
